<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, shallowRef } from 'vue'

import type { TixDate } from '@tix/tix-time'

import CalendarLegend from './CalendarLegend.vue'
import CalendarMonth from './CalendarMonth.vue'
import CalendarScrollButton from './CalendarScrollButton.vue'
import { findClosestEnabledDate, scrollButtonHandler } from './helpers'
import type { CalendarMonthData, DayData } from './types'

interface Props {
  /**
   * Index of DayData by date string (YYYY-MM-DD). Keys must be in chronological order.
   * Must not be changed after mount. The picker does not react to index changes; doing so
   * can leave internal focus state stale and cause errors. For a new date range, mount a
   * new instance (e.g. use :key tied to the range).
   */
  index: Record<string, DayData>
  months: Record<string, CalendarMonthData>
  displayPrices: boolean
  capacityThreshold: number | false
  showLegend?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  select: [day: DayData]
}>()

// Internal UI state
const wrapperRef = ref<HTMLElement | null>(null)
const calendarRef = ref<HTMLElement | null>(null)
const scrollObserver = ref<IntersectionObserver | null>(null)
const focusedDate = shallowRef<TixDate | null>(null)
const visibleMonths = ref<Set<string>>(new Set())
const enabledButtons = ref({
  prev: false,
  next: true,
})

// Computed properties
const days = computed(() => Object.values(props.index))
const monthIds = computed(() => Object.keys(props.months))
const firstMonth = computed(() => monthIds.value[0])
const lastMonth = computed(() => monthIds.value[monthIds.value.length - 1])
const isSingleMonth = computed(() => monthIds.value.length === 1)

const firstAvailableDate = computed(() => {
  return days.value.find((day) => day.enabled)?.date
})

// Arrow key offset mapping
function arrowKeyOffset(keyCode: string): number | undefined {
  const offsets: Record<string, number> = {
    ArrowUp: -7,
    ArrowRight: +1,
    ArrowDown: +7,
    ArrowLeft: -1,
  }
  return offsets[keyCode]
}

const INDEX_CHANGED_MESSAGE =
  'CalendarPicker: index prop must not change after mount. Focused date is no longer in the current index. Use a new component instance (e.g. :key="dateRange") for a different date range.'

function assertFocusedDateInIndex(): void {
  /* v8 ignore if defensive -- @preserve */
  if (!focusedDate.value) return
  /* v8 ignore if defensive -- @preserve */
  if (focusedDate.value.date in props.index) return
  /* v8 ignore next changing the index prop is not supported -- @preserve */
  throw new Error(INDEX_CHANGED_MESSAGE)
}

// Keyboard navigation handler — only when focus is on a day cell (not scroll buttons or other controls)
function handleKeydown(e: KeyboardEvent) {
  /* v8 ignore if focus outside picker not reachable in Storybook iframe -- @preserve */
  if (!wrapperRef.value?.contains(document.activeElement)) {
    return
  }
  const isDayFocused = (document.activeElement as HTMLElement | null)?.closest?.('.picker-day') != null
  const offset = arrowKeyOffset(e.code)
  /* v8 ignore else only arrow-key path exercised in storybook -- @preserve */
  if (offset) {
    if (!isDayFocused) return
    e.preventDefault()
    /* v8 ignore else defensive; focusedDate set on mount -- @preserve */
    if (focusedDate.value) {
      assertFocusedDateInIndex()
      const closest = findClosestEnabledDate(props.index, focusedDate.value, offset)
      if (closest) {
        focusedDate.value = closest
      }
    }
    // Scroll to the focused date anyway, to bring it back into the viewport
    /* v8 ignore else defensive; focusedDate set on mount -- @preserve */
    if (focusedDate.value) {
      scrollToMonth(focusedDate.value)
    }
  } else if (e.code === 'Enter') {
    /* v8 ignore if defensive -- @preserve */
    if (!isDayFocused) return
    e.preventDefault()
    /* v8 ignore else defensive; focusedDate set on mount -- @preserve */
    if (focusedDate.value) {
      assertFocusedDateInIndex()
      const day = props.index[focusedDate.value.date]
      /* v8 ignore else defensive; index has entry when focused -- @preserve */
      if (day) {
        emit('select', day)
      }
    }
  }
}

// Scroll to a specific month
function scrollToMonth(date: TixDate) {
  /* v8 ignore if ref guard: calendarRef set before use -- @preserve */
  if (!calendarRef.value) return

  const key = date.format('YYYY-MM')
  const month = calendarRef.value.querySelector(`.picker-month[data-id="${key}"]`)

  /* v8 ignore else month element always present in storybook -- @preserve */
  if (month) {
    // Use nextTick equivalent with setTimeout
    setTimeout(() => {
      month.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    }, 0)
  }
}

// Set up intersection observer for scroll tracking
function observeScrolling() {
  /* v8 ignore if ref guard: calendarRef set before use -- @preserve */
  if (!calendarRef.value) return

  const handleScroll: IntersectionObserverCallback = (entries) => {
    for (const entry of entries) {
      const month = entry.target.getAttribute('data-id')
      /* v8 ignore else observed elements have data-id -- @preserve */
      if (month) {
        if (entry.isIntersecting) {
          visibleMonths.value.add(month)
        } else {
          visibleMonths.value.delete(month)
        }
      }
    }

    enabledButtons.value = {
      prev: !visibleMonths.value.has(firstMonth.value),
      next: !visibleMonths.value.has(lastMonth.value),
    }
  }

  const observer = new IntersectionObserver(handleScroll, {
    root: calendarRef.value,
    rootMargin: '0px',
    threshold: 0.4,
  })
  scrollObserver.value = observer

  for (const monthId of monthIds.value) {
    const el = calendarRef.value.querySelector(`.picker-month[data-id="${monthId}"]`)
    /* v8 ignore else month elements present in storybook -- @preserve */
    if (el) {
      observer.observe(el)
    }
  }
}

// Handle scroll button press
function handleScrollButtonPress(offset: number) {
  /* v8 ignore else ref guard; set before use -- @preserve */
  if (wrapperRef.value) {
    scrollButtonHandler(wrapperRef.value, visibleMonths.value, offset)
  }
}

// Handle day focus
function onFocus(day: DayData) {
  focusedDate.value = day.date
}

// Handle day selection
function onSelect(day: DayData) {
  emit('select', day)
}

// Lifecycle
onMounted(() => {
  focusedDate.value = firstAvailableDate.value!
  window.addEventListener('keydown', handleKeydown)
  observeScrolling()

  /* v8 ignore else defensive; firstAvailableDate set when index non-empty -- @preserve */
  if (focusedDate.value) {
    scrollToMonth(focusedDate.value)
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  scrollObserver.value?.disconnect()
  scrollObserver.value = null
})
</script>

<template>
  <div ref="wrapperRef" class="calendar-picker-wrapper" :class="{ 'single-month': isSingleMonth }">
    <div class="nav-buttons">
      <CalendarScrollButton direction="prev" :enabled="enabledButtons.prev" @scroll="handleScrollButtonPress" />
      <CalendarScrollButton direction="next" :enabled="enabledButtons.next" @scroll="handleScrollButtonPress" />
    </div>

    <table class="picker-calendar-mobile-header" aria-hidden="true">
      <thead>
        <tr>
          <th><abbr title="Sunday">S</abbr></th>
          <th><abbr title="Monday">M</abbr></th>
          <th><abbr title="Tuesday">T</abbr></th>
          <th><abbr title="Wednesday">W</abbr></th>
          <th><abbr title="Thursday">T</abbr></th>
          <th><abbr title="Friday">F</abbr></th>
          <th><abbr title="Saturday">S</abbr></th>
        </tr>
      </thead>
    </table>

    <ul ref="calendarRef" aria-label="Calendar" class="calendar">
      <CalendarMonth
        v-for="(month, key) in months"
        :key="key"
        :capacityThreshold="capacityThreshold"
        :displayPrices="displayPrices"
        :focusedDate="focusedDate ?? undefined"
        :month="month"
        @focus="onFocus"
        @select="onSelect"
      />
    </ul>
    <CalendarLegend v-if="showLegend" :are-prices-displayed="displayPrices" :days="days" />
  </div>
</template>

<style scoped>
.calendar-picker-wrapper {
  position: relative;

  .picker-calendar-mobile-header {
    display: none;

    @media (--mobile-experience) {
      display: table;
      width: 100%;
      margin-bottom: 0;
      border-collapse: collapse;
      border: none;

      th {
        overflow: hidden;
        border-bottom: 1px solid var(--neutral-border-color);
        padding: 0;
        margin: 0;
        width: 14.2857%;

        abbr {
          display: block;
          width: 100%;
          padding: 12px 0;
          text-align: center;
          text-decoration: none;
        }
      }
    }
  }

  .nav-buttons {
    @media (--mobile-experience) {
      display: none;
    }
  }

  .calendar {
    margin: 0;
    padding: 0;
    display: grid;
    grid-auto-flow: column;
    line-height: 16.5px;
    width: 100%;
    overflow-y: hidden;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scroll-snap-type: x mandatory;

    @media (--mobile-experience) {
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior-x: hidden;
      overscroll-behavior-y: contain;
      scroll-snap-type: y mandatory;
      grid-auto-flow: row;
      height: 495px;
      margin-top: 0;
    }

    :deep(li) {
      display: inline-block;
      scroll-snap-align: start;

      @media (--mobile-experience) {
        width: 100%;
        scroll-snap-align: center;
        margin: 0;
      }
    }
  }
}
</style>
